import { useState } from "react";
import { GOLD, NAVY, WHITE } from "../styles/palette";
import { sb, sbAuth } from "../lib/supabase";
import { INVITE_CODE } from "../lib/constants";
import { ScoopMark } from "./ScoopMark";
import { useIsMobile } from "../lib/useIsMobile";

export function AuthPage({ onLogin }) {
  const isMobile = useIsMobile();
  const [mode,       setMode]       = useState("login"); // "login" | "register"
  const [email,      setEmail]      = useState("");
  const [password,   setPassword]   = useState("");
  const [confirmPw,  setConfirmPw]  = useState("");
  const [companyName,setCompanyName]= useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error,      setError]      = useState("");
  const [loading,    setLoading]    = useState(false);
  const [success,    setSuccess]    = useState("");
  // Forgot-password: a small inline flow shown from LOGIN mode. We ALWAYS show a
  // generic confirmation (never reveal whether the email exists). The request is
  // sent through the public request-password-reset edge function.
  const [forgot,        setForgot]        = useState(false);
  const [forgotEmail,   setForgotEmail]   = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent,    setForgotSent]    = useState(false);

  async function handleForgot(e) {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await sb.fn("request-password-reset", {
        email: forgotEmail.trim(),
        redirectTo: window.location.origin + "/reset-password",
      });
    } catch (err) {
      // Swallow — anti-enumeration: the user sees the same generic message
      // whether or not the email exists or the request errored.
    }
    setForgotSent(true);
    setForgotLoading(false);
  }

  async function handleLogin(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const session = await sbAuth.signIn(email, password);
      onLogin(session);
    } catch(err) {
      setError(err.message || "Invalid email or password");
    }
    setLoading(false);
  }

  async function handleRegister(e) {
    e.preventDefault();
    setError(""); setSuccess("");
    if (!INVITE_CODE || inviteCode.trim() !== INVITE_CODE) { setError("Invalid invite code. Please contact your administrator."); return; }
    if (!companyName.trim())                { setError("Please enter your company name"); return; }
    if (password !== confirmPw)             { setError("Passwords do not match"); return; }
    if (password.length < 8)               { setError("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      await sbAuth.signUp(email, password, companyName, inviteCode.trim());
      setSuccess("Account created! You can now sign in.");
      setMode("login");
      setPassword(""); setConfirmPw(""); setInviteCode(""); setCompanyName("");
    } catch(err) {
      setError(err.message || "Registration failed. This email may already be registered.");
    }
    setLoading(false);
  }

  // ── Shared styles (phone-aware). On a phone every input/button is a ≥44px
  // target and every text "link" becomes a real button with a padded tap area.
  // Desktop values are unchanged from the original layout.
  const inputStyle = {
    width:"100%", padding:isMobile?"12px 14px":"10px 12px", border:"1px solid #dfe1e6", borderRadius:isMobile?8:6,
    fontSize:13, fontFamily:"Inter,sans-serif", outline:"none", transition:".12s", boxSizing:"border-box", minWidth:0,
  };
  const labelStyle = { display:"block", fontSize:isMobile?13:12, fontWeight:500, color:"#42526e", marginBottom:5 };
  const hintStyle  = { fontSize:isMobile?12:11, color:"#8993a4", marginTop:4 };
  const primaryBtn = (busy) => ({
    width:"100%", padding:isMobile?"13px":"11px", minHeight:isMobile?46:undefined, background:busy?"#c5cdd8":NAVY, color:WHITE, border:"none",
    borderRadius:isMobile?8:6, fontSize:isMobile?15:14, fontWeight:600, fontFamily:"Inter,sans-serif", cursor:busy?"not-allowed":"pointer",
    transition:".12s", letterSpacing:"-.1px",
  });
  // Inline text link rendered as a <button>: looks like the old span on desktop,
  // grows a 36px+ padded tap area on phones.
  const linkBtn = {
    background:"none", border:"none", color:NAVY, fontWeight:600, cursor:"pointer", fontFamily:"inherit",
    fontSize:isMobile?14:12, padding:isMobile?"8px 10px":0, minHeight:isMobile?36:undefined, display:"inline-block", lineHeight:1.3,
  };
  const focusOn  = e=>{ e.target.style.borderColor=NAVY; };
  const focusOff = e=>{ e.target.style.borderColor="#dfe1e6"; };

  return (
    <div style={{minHeight:isMobile?"100dvh":"100vh",display:"flex",background:"#f5f6f8"}}>
      {/* Left panel — hidden on mobile so the form gets the full width */}
      {!isMobile && (
      <div style={{width:420,background:NAVY,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:48,flexShrink:0}}>
        <div style={{marginBottom:32,textAlign:"center"}}>
          <div style={{textAlign:"center",marginBottom:12}}><ScoopMark size={52} /></div>
          <div style={{fontFamily:"Inter,sans-serif",fontSize:26,fontWeight:800,color:WHITE,letterSpacing:"-.5px"}}>
            iScoop<span style={{color:GOLD}}>CRM</span>
          </div>
          <div style={{color:"rgba(255,255,255,.45)",fontSize:13,marginTop:6}}>Scoopers on Doody</div>
        </div>

        <div style={{width:"100%",marginBottom:32}}>
          {[
            {icon:"🔒", text:"Secure Stripe billing"},
            {icon:"📍", text:"Smart route optimization"},
            {icon:"📋", text:"Automated monthly invoicing"},
            {icon:"🗺️", text:"Live client map"},
          ].map(f=>(
            <div key={f.text} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid rgba(255,255,255,.06)"}}>
              <span style={{fontSize:16}}>{f.icon}</span>
              <span style={{color:"rgba(255,255,255,.6)",fontSize:13}}>{f.text}</span>
            </div>
          ))}
        </div>

        <div style={{color:"rgba(255,255,255,.2)",fontSize:11,textAlign:"center"}}>
          iScoopCRM v7.0 · Built for your business
        </div>
      </div>
      )}

      {/* Right panel — form. On a phone it pins to the top (a vertically-centred
          form jumps when the keyboard opens) and respects the notch/home-bar. */}
      <div style={{flex:1,minWidth:0,display:"flex",alignItems:isMobile?"flex-start":"center",justifyContent:"center",
        padding:isMobile?"calc(24px + env(safe-area-inset-top)) 16px calc(32px + env(safe-area-inset-bottom))":40}}>
        <div style={{width:"100%",maxWidth:400,minWidth:0}}>
          {/* Phone brand header — the navy brand panel is hidden on phones, so
              the mark + wordmark live above the form instead. */}
          {isMobile && (
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:22}}>
              <ScoopMark size={36} />
              <div>
                <div style={{fontFamily:"Inter,sans-serif",fontSize:20,fontWeight:800,color:NAVY,letterSpacing:"-.4px",lineHeight:1.1}}>
                  iScoop<span style={{color:GOLD}}>CRM</span>
                </div>
                <div style={{fontSize:12,color:"#8993a4",marginTop:2}}>Scoopers on Doody</div>
              </div>
            </div>
          )}

          {/* Tab switcher */}
          <div style={{display:"flex",background:"#e8ecf8",borderRadius:8,padding:4,marginBottom:isMobile?22:28}}>
            {[["login","Sign In"],["register","Register"]].map(([m,label])=>(
              <button key={m} type="button"
                onClick={()=>{setMode(m);setError("");setSuccess("");setForgot(false);setForgotSent(false);}}
                aria-pressed={mode===m}
                style={{flex:1,minWidth:0,textAlign:"center",padding:isMobile?"11px 8px":"8px",minHeight:isMobile?44:undefined,border:"none",borderRadius:6,cursor:"pointer",
                  fontSize:isMobile?14:13,fontWeight:mode===m?600:500,fontFamily:"inherit",lineHeight:"normal",
                  background:mode===m?WHITE:"transparent",color:mode===m?NAVY:"#8993a4",
                  boxShadow:mode===m?"0 1px 3px rgba(7,44,118,.12)":"none",transition:".12s"}}>
                {label}
              </button>
            ))}
          </div>

          <div style={{marginBottom:isMobile?20:24}}>
            <div style={{fontFamily:"Inter,sans-serif",fontSize:22,fontWeight:700,color:"#1a1f36",letterSpacing:"-.3px"}}>
              {forgot?"Reset your password":(mode==="login"?"Welcome back":"Create your account")}
            </div>
            <div style={{fontSize:13,color:"#8993a4",marginTop:4}}>
              {forgot?"Enter your email and we'll send you a reset link":(mode==="login"?"Sign in to your iScoopCRM account":"You'll need an invite code to register")}
            </div>
          </div>

          {error   && !forgot && <div style={{background:"#ffebe6",border:"1px solid #ffbdad",borderRadius:6,padding:"10px 14px",fontSize:13,color:"#bf2600",marginBottom:16}}>⚠️ {error}</div>}
          {success && !forgot && <div style={{background:"#e3fcef",border:"1px solid #abf5d1",borderRadius:6,padding:"10px 14px",fontSize:13,color:"#006644",marginBottom:16}}>✅ {success}</div>}

          {/* ── Forgot-password (inline) ─────────────────────────────── */}
          {forgot ? (
            forgotSent ? (
              <>
                <div style={{background:"#e3fcef",border:"1px solid #abf5d1",borderRadius:6,padding:"10px 14px",fontSize:13,color:"#006644",marginBottom:16}}>
                  ✅ If an account exists for that email, we just sent a reset link — check your inbox.
                </div>
                <button type="button"
                  onClick={()=>{setForgot(false);setForgotSent(false);setForgotEmail("");setError("");setSuccess("");}}
                  style={primaryBtn(false)}>
                  ← Back to sign in
                </button>
              </>
            ) : (
              <form onSubmit={handleForgot}>
                <div style={{marginBottom:20}}>
                  <label style={labelStyle}>Email address</label>
                  <input type="email" value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)} required
                    placeholder="you@example.com" autoComplete="username" inputMode="email" autoCapitalize="none" spellCheck={false}
                    style={inputStyle}
                    onFocus={focusOn}
                    onBlur={focusOff}
                  />
                </div>
                <button type="submit" disabled={forgotLoading} style={primaryBtn(forgotLoading)}>
                  {forgotLoading?"Sending...":"Send reset link"}
                </button>
                <div style={{marginTop:isMobile?12:16,textAlign:"center",fontSize:isMobile?14:12,color:"#8993a4"}}>
                  <button type="button" style={linkBtn} onClick={()=>{setForgot(false);setForgotEmail("");setError("");}}>← Back to sign in</button>
                </div>
              </form>
            )
          ) : (
          <form onSubmit={mode==="login"?handleLogin:handleRegister}>
            <div style={{marginBottom:14}}>
              <label style={labelStyle}>Email address</label>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)} required
                placeholder="you@example.com" autoComplete="username" inputMode="email" autoCapitalize="none" spellCheck={false}
                style={inputStyle}
                onFocus={focusOn}
                onBlur={focusOff}
              />
            </div>

            <div style={{marginBottom:14}}>
              <label style={labelStyle}>Password</label>
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)} required
                placeholder={mode==="register"?"Min 8 characters":"••••••••"}
                autoComplete={mode==="register"?"new-password":"current-password"}
                style={inputStyle}
                onFocus={focusOn}
                onBlur={focusOff}
              />
            </div>

            {mode==="register" && <>
              <div style={{marginBottom:14}}>
                <label style={labelStyle}>Confirm Password</label>
                <input type="password" value={confirmPw} onChange={e=>setConfirmPw(e.target.value)} required
                  placeholder="••••••••" autoComplete="new-password"
                  style={inputStyle}
                  onFocus={focusOn}
                  onBlur={focusOff}
                />
              </div>
              <div style={{marginBottom:14}}>
                <label style={labelStyle}>Company name</label>
                <input type="text" value={companyName} onChange={e=>setCompanyName(e.target.value)} required
                  placeholder="e.g. Scoopers on Doody" autoComplete="organization"
                  style={inputStyle}
                  onFocus={focusOn}
                  onBlur={focusOff}
                />
                <div style={hintStyle}>This creates your own company workspace</div>
              </div>
              <div style={{marginBottom:20}}>
                <label style={labelStyle}>Invite Code</label>
                <input type="text" value={inviteCode} onChange={e=>setInviteCode(e.target.value)} required
                  placeholder="Enter your invite code" autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                  style={inputStyle}
                  onFocus={focusOn}
                  onBlur={focusOff}
                />
                <div style={hintStyle}>Contact your administrator for the invite code</div>
              </div>
            </>}

            {mode==="login" && (
              <div style={{textAlign:"right",marginTop:isMobile?0:-4,marginBottom:14,marginRight:isMobile?-10:0}}>
                <button type="button" style={linkBtn}
                  onClick={()=>{setForgot(true);setForgotSent(false);setForgotEmail(email);setError("");setSuccess("");}}>
                  Forgot password?
                </button>
              </div>
            )}

            <button type="submit" disabled={loading} style={primaryBtn(loading)}>
              {loading?(mode==="login"?"Signing in...":"Creating account..."):(mode==="login"?"Sign In →":"Create Account")}
            </button>
          </form>
          )}

          {!forgot && (
          <div style={{marginTop:isMobile?16:20,textAlign:"center",fontSize:isMobile?14:12,color:"#8993a4"}}>
            {mode==="login"
              ? <>Don't have an account? <button type="button" style={linkBtn} onClick={()=>{setMode("register");setError("");}}>Register</button></>
              : <>Already have an account? <button type="button" style={linkBtn} onClick={()=>{setMode("login");setError("");}}>Sign in</button></>
            }
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
